import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthenticateProvider";
import { connectSocket, disconnectSocket, getSocket } from "../Helpers/socketClient";

const SocketPresenceContext = createContext(null);

// Centralised socket manager for the whole admin app. Everything the spec
// asks for lives here in one place:
//  - opens the one shared socket connection as soon as isAuthenticated goes
//    true, and tears it down (emitting admin:offline first) the moment it
//    goes false - covers logout, and a hard page reload/close is covered
//    separately since the browser itself kills the transport, which the
//    backend's own 'disconnect' handler treats identically.
//  - Page Visibility API: a single `visibilitychange` listener emits
//    admin:away/admin:online as the tab is hidden/shown again.
//  - reconnection: socket.io-client's built-in reconnection fires 'connect'
//    again on every successful reconnect, and the handler below re-sends
//    the current page + visibility exactly as it does on first connect -
//    that's "restore state after a dropped connection" with no special-case
//    code needed.
function SocketPresenceProvider({ children }) {
    const { isAuthenticated } = useAuth();
    const [connectionStatus, setConnectionStatus] = useState("offline"); // "online" | "away" | "offline"
    const currentPageRef = useRef(window.location?.pathname || "/");

    const emitVisibility = useCallback((socket) => {
        if (!socket?.connected) return;
        if (document.visibilityState === "hidden") {
            socket.emit("admin:away");
            setConnectionStatus("away");
        } else {
            socket.emit("admin:online", { page: currentPageRef.current });
            setConnectionStatus("online");
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            disconnectSocket();
            setConnectionStatus("offline");
            return undefined;
        }

        const socket = connectSocket();

        const handleConnect = () => emitVisibility(socket);
        const handleDisconnect = () => setConnectionStatus("offline");
        const handleVisibilityChange = () => emitVisibility(socket);

        socket.on("connect", handleConnect);
        socket.on("disconnect", handleDisconnect);
        socket.on("connect_error", handleDisconnect);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        if (socket.connected) emitVisibility(socket);

        return () => {
            socket.off("connect", handleConnect);
            socket.off("disconnect", handleDisconnect);
            socket.off("connect_error", handleDisconnect);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [isAuthenticated, emitVisibility]);

    // Best-effort notice for the "browser closed/refreshed" case - fires a
    // beat before the transport actually dies. Not load-bearing: the
    // backend's socket 'disconnect' handler marks the admin offline
    // regardless of whether this emit lands in time.
    useEffect(() => {
        const handlePageHide = () => {
            const socket = getSocket();
            if (socket.connected) socket.emit("admin:offline");
        };
        window.addEventListener("pagehide", handlePageHide);
        return () => window.removeEventListener("pagehide", handlePageHide);
    }, []);

    const reportPageChange = useCallback((pathname) => {
        currentPageRef.current = pathname;
        const socket = getSocket();
        if (socket.connected) socket.emit("admin:page-change", { page: pathname });
    }, []);

    return (
        <SocketPresenceContext.Provider value={{ connectionStatus, reportPageChange }}>
            {children}
        </SocketPresenceContext.Provider>
    );
}

export default SocketPresenceProvider;

export const useSocketPresence = () => useContext(SocketPresenceContext);
